/**
 * Rinnovo di un'offerta per l'anno successivo.
 *
 * Le offerte di assistenza si ripetono ogni anno quasi uguali: si riaprivano,
 * si ricopiavano a mano e si cambiavano le date, dimenticando ogni tanto un
 * pezzo. Qui si duplica tutto — contenuti, preventivo, listino — e si decide
 * una cosa sola: quanto adeguare i prezzi.
 *
 * L'adeguamento ISTAT si propone dal dato in archivio (aggiornato dall'ISTAT
 * quando risponde) e si applica per intero o in parte, come si concorda con i
 * clienti di vecchia data.
 */
import { useEffect, useState } from 'react';
import { offerteApi, istatApi } from '../api';
import Modal from './Modal';

const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const oggi = () => new Date().toISOString().slice(0, 10);

export default function RinnovoOfferta({ offerta, onFatto, onClose }) {
  const annoOfferta = new Date(offerta.data_emissione || Date.now()).getFullYear();
  const [anno, setAnno] = useState(annoOfferta + 1);
  const [numero, setNumero] = useState('');
  const [variazione, setVariazione] = useState('');
  const [quota, setQuota] = useState(100);
  const [istat, setIstat] = useState(null);
  const [manuale, setManuale] = useState(false);
  const [lavorando, setLavorando] = useState(false);
  const [errore, setErrore] = useState('');

  useEffect(() => {
    istatApi.corrente()
      .then(d => {
        setIstat(d);
        if (d?.disponibile) setVariazione(String(d.variazione_annua).replace('.', ','));
      })
      .catch(() => setIstat({ disponibile: false, messaggio: 'Dato ISTAT non raggiungibile.' }));
  }, []);

  // Numero proposto per l'anno nuovo: lo stesso progressivo non andrebbe bene,
  // perché il contatore riparte da 01 a ogni anno.
  useEffect(() => {
    offerteApi.nextNumero(offerta.user_id, anno)
      .then(d => setNumero(d.numero))
      .catch(() => {});
  }, [anno, offerta.user_id]);

  const v = Number(String(variazione).replace(',', '.')) || 0;
  const applicata = +(v * (Number(quota) || 0) / 100).toFixed(4);
  const nuovoImporto = Number(offerta.importo || 0) * (1 + applicata / 100);

  const salvaIstatAMano = async () => {
    const periodo = prompt('Periodo del dato ISTAT (AAAA-MM):', new Date().toISOString().slice(0, 7));
    if (!periodo) return;
    try {
      const d = await istatApi.salva({ periodo, variazione_annua: v });
      setIstat(d); setManuale(false);
    } catch (e) { setErrore(String(e?.error || e)); }
  };

  const rinnova = async () => {
    setLavorando(true); setErrore('');
    try {
      const nuova = await offerteApi.rinnova(offerta.id, {
        anno, numero: numero.trim(), variazione: v, quota: Number(quota) || 0,
        data_emissione: oggi(),
      });
      onFatto?.(nuova);
    } catch (e) { setErrore(String(e?.error || e)); } finally { setLavorando(false); }
  };

  return (
    <Modal title={`Rinnova l'offerta ${offerta.numero}`} onClose={onClose} size="lg">
      <div className="space-y-4 text-sm">
        <p className="text-xs text-gray-500">
          Nasce una nuova offerta <strong>in bozza</strong> per <strong>{offerta.cliente_nome}</strong>,
          con gli stessi contenuti, lo stesso preventivo e lo stesso listino. Resta da rivederla e
          inviarla: il rinnovo si fa solo quando lo chiedi tu, niente parte da solo.
        </p>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">Anno</label>
            <input className="input" type="number" min={annoOfferta} max={annoOfferta + 5}
                   value={anno} onChange={e => setAnno(Number(e.target.value))} />
          </div>
          <div className="col-span-2">
            <label className="label">Numero della nuova offerta</label>
            <input className="input font-mono" value={numero} onChange={e => setNumero(e.target.value)} />
          </div>
        </div>

        {/* Adeguamento ISTAT */}
        <div className="border rounded-lg p-3 space-y-2 bg-gray-50">
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Adeguamento ISTAT
            </span>
            {istat?.disponibile && (
              <span className="text-xs text-gray-500">
                {istat.fonte} · dato di {istat.periodo}
              </span>
            )}
          </div>

          {istat === null && <p className="text-xs text-gray-400">Leggo l'indice…</p>}
          {istat && !istat.disponibile && (
            <p className="text-xs text-amber-700">{istat.messaggio}</p>
          )}

          <div className="grid grid-cols-3 gap-3 items-end">
            <div>
              <label className="label text-xs">Variazione annua (%)</label>
              <input className="input py-1 text-sm text-right" inputMode="decimal"
                     value={variazione} onChange={e => { setVariazione(e.target.value); setManuale(true); }} />
            </div>
            <div className="col-span-2">
              <label className="label text-xs">Quanta parte applicarne: {quota}%</label>
              <input type="range" min="0" max="100" step="5" className="w-full"
                     value={quota} onChange={e => setQuota(Number(e.target.value))} />
              <div className="flex gap-1 mt-1">
                {[0, 50, 100].map(q => (
                  <button key={q} type="button" className="btn-secondary btn-sm"
                          onClick={() => setQuota(q)}>
                    {q === 0 ? 'Nessuno' : q === 100 ? 'Intero' : 'Metà'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {manuale && v !== 0 && (
            <button type="button" className="text-xs text-brand-600 hover:underline"
                    onClick={salvaIstatAMano}>
              Conserva questo valore per i prossimi rinnovi
            </button>
          )}

          <p className="text-xs text-gray-600 border-t pt-2">
            {applicata === 0
              ? 'Rinnovo agli stessi prezzi: tariffe e importo restano quelli dell’offerta di partenza.'
              : <>Si applica <strong>{String(applicata).replace('.', ',')}%</strong> a tariffe,
                 prezzi liberi e listino. Importo: {fmt(offerta.importo)} → <strong className="text-brand-700">{fmt(nuovoImporto)}</strong>.</>}
          </p>
        </div>

        {errore && (
          <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-700">{errore}</div>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button className="btn-secondary" onClick={onClose}>Annulla</button>
        <button className="btn-primary" disabled={lavorando || !numero.trim()} onClick={rinnova}>
          {lavorando ? 'Creo…' : 'Crea il rinnovo'}
        </button>
      </div>
    </Modal>
  );
}
